import { ApolloError } from '@apollo/client';
import { FormField, Spinner, Text } from '@dixa/lib-shared-habla';
import { HookFormText } from 'lib-shared-hook-form';

import {
  PartnerSettingsConfiguration,
  PartnerSettingsConfigurationSchema,
} from 'apis/partnerIntegrations';

import { ThirdPartySettingsConfigurationSchemaField } from './components/schemaField/thirdPartySettingsConfigurationSchemaField';

type ThirdPartySettingsConfigurationProps = {
  mode: 'create' | 'update';
  integrationType: string;
  integrationId?: string;
  schema: PartnerSettingsConfigurationSchema[];
  isSchemaLoading: boolean;
  schemaError?: ApolloError;
  initialValues?: PartnerSettingsConfiguration;
};

/**
 * A component for rendering the settings configuration form of a 3rd party
 * integration.
 *
 * @param mode - The mode of the form.
 * @param integrationType - The type of the integration.
 * @param integrationId - The id of the integration in case it already exists.
 * @param schema - The settings configuration schema fields to render.
 * @param isSchemaLoading - Whether the schema is still being loaded.
 * @param schemaError - The error that occurred while loading the schema.
 * @param initialValues - The initial values of the integration in case we want
 *   to update an existing integration.
 * @returns The form fields for the settings configuration.
 */
export const ThirdPartySettingsConfiguration = ({
  mode,
  integrationType,
  integrationId,
  schema,
  isSchemaLoading,
  schemaError,
  initialValues,
}: ThirdPartySettingsConfigurationProps): JSX.Element => {
  const isCreateMode = mode === 'create';
  const hasNoIntegrationToUpdate = !initialValues && mode === 'update';

  if (hasNoIntegrationToUpdate) {
    return (
      <FormField>
        <Text.Paragraph>
          Something went wrong, there is no integration to update. Please
          contact support.
        </Text.Paragraph>
      </FormField>
    );
  }

  if (schemaError) {
    return (
      <FormField>
        <Text.Paragraph>
          Something went wrong. Please try again later or contact support.
        </Text.Paragraph>
      </FormField>
    );
  }

  return isSchemaLoading ? (
    <Spinner />
  ) : (
    <>
      <FormField>
        <HookFormText
          label="Name"
          name="name.value"
          rules={{
            required: 'The name is required',
          }}
          componentProps={{
            placeholder: 'Enter a name for your integration',
          }}
        />
      </FormField>

      {schema.map((field) => (
        <ThirdPartySettingsConfigurationSchemaField
          key={field.key}
          isCreateMode={isCreateMode}
          integrationType={integrationType}
          integrationId={integrationId}
          field={field}
        />
      ))}
    </>
  );
};
